import { notification } from "antd"
import type { FormInstance } from "antd/es/form/hooks/useForm"
import { type ValidateErrorEntity } from "rc-field-form/es/interface"

import { notificationOptions } from "../../../constants/notification.config"
import i18n from "../../../i18n.ts"

const MAX_VISIBLE_REASONS = 4

function isFormValidationError(error: unknown): error is ValidateErrorEntity {
  return Array.isArray((error as ValidateErrorEntity)?.errorFields)
}

function describeReasons(reasons: string[]) {
  if (reasons.length <= 1) return reasons[0]

  // A "+1 more" line occupies the row the reason itself would have used, so the
  // list is only capped once eliding actually buys space.
  const capped = reasons.length > MAX_VISIBLE_REASONS + 1
  const visible = capped ? reasons.slice(0, MAX_VISIBLE_REASONS) : reasons
  const hidden = reasons.length - visible.length

  return (
    <ul className="m-0 list-disc pl-5">
      {visible.map((reason, index) => (
        <li key={`${index}-${reason}`}>{reason}</li>
      ))}
      {hidden > 0 && (
        <li className="list-none pl-0">
          {
            i18n.t("documentTemplates.validation.moreReasons", {
              count: hidden,
              defaultValue: "+{{count}} more"
            }) as string
          }
        </li>
      )}
    </ul>
  )
}

function showDocumentValidationError(
  form: FormInstance,
  error: unknown
): boolean {
  if (!isFormValidationError(error)) return false

  const invalidFields = error.errorFields.filter(
    (field) => field.errors?.length
  )
  const [firstField] = invalidFields
  if (!firstField) return false

  form.scrollToField(firstField.name, {
    behavior: "smooth",
    block: "center",
    scrollMode: "always"
  })

  notification.warning({
    ...notificationOptions.warning,
    key: "document_validation_error",
    message: "The document cannot be saved",
    // Duplicates stay: `phone` and `phone_2` share a message, and collapsing
    // them would refuse the next submit for the field the user did not fix.
    // Blanks go: a rule written `message: ""` arrives as "", not as a default.
    description: describeReasons(
      invalidFields
        .flatMap((field) => field.errors ?? [])
        .filter((reason) => reason.trim())
    )
  })

  return true
}

export { isFormValidationError, showDocumentValidationError }
